import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, Trash2, Edit3, DoorOpen, Bed as BedIcon, Loader2, ChevronDown, ChevronRight, Package } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { CurrencyLabel } from '../CurrencyDisplay';

/**
 * Admin management of rooms and beds within a housing unit.
 * Structure: Housing → Rooms → Beds (upper/lower + price + locker number).
 * Props:
 *   open           boolean       — modal open
 *   onOpenChange   fn            — close handler
 *   housing        object        — { id, name }
 *   onChanged      fn (optional) — called after any mutation so parent can refresh
 */
const RoomsAndBedsManager = ({ open, onOpenChange, housing, onChanged }) => {
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRoom, setExpandedRoom] = useState(null);

  // Room form
  const [newRoom, setNewRoom] = useState({ name: '', capacity: '', notes: '' });
  const [editingRoom, setEditingRoom] = useState(null); // {id, name, capacity, notes}

  // Bed form
  const [newBed, setNewBed] = useState({ bed_number: '', bed_type: 'lower', price: '500', locker_number: '' });
  const [editingBed, setEditingBed] = useState(null); // {id, ...}

  const load = useCallback(async () => {
    if (!housing?.id) return;
    setLoading(true);
    try {
      const [r, b] = await Promise.all([
        api.get(`/housings/${housing.id}/rooms`),
        api.get(`/housings/${housing.id}/beds`),
      ]);
      setRooms(r.data || []);
      setBeds(b.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تحميل الغرف والأسرّة');
    } finally {
      setLoading(false);
    }
  }, [housing?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const notifyChanged = () => { if (typeof onChanged === 'function') onChanged(); };

  // === Rooms ===
  const addRoom = async () => {
    const name = newRoom.name.trim();
    if (!name) { toast.error('اسم الغرفة مطلوب'); return; }
    try {
      await api.post(`/housings/${housing.id}/rooms`, {
        name,
        capacity: Number(newRoom.capacity || 0),
        notes: newRoom.notes || null,
      });
      setNewRoom({ name: '', capacity: '', notes: '' });
      toast.success('تمت إضافة الغرفة');
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل إضافة الغرفة');
    }
  };

  const saveRoomEdit = async () => {
    if (!editingRoom) return;
    try {
      await api.put(`/housings/${housing.id}/rooms/${editingRoom.id}`, {
        name: editingRoom.name,
        capacity: Number(editingRoom.capacity || 0),
        notes: editingRoom.notes || null,
      });
      toast.success('تم حفظ التعديل');
      setEditingRoom(null);
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحفظ');
    }
  };

  const deleteRoom = async (room) => {
    if (!window.confirm(`حذف الغرفة "${room.name}"؟`)) return;
    try {
      await api.delete(`/housings/${housing.id}/rooms/${room.id}`);
      toast.success('تم حذف الغرفة');
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحذف');
    }
  };

  // === Beds ===
  const addBed = async (roomId) => {
    const num = newBed.bed_number.trim();
    const locker = (newBed.locker_number || '').trim();
    if (!num) { toast.error('رقم السرير مطلوب'); return; }
    if (!locker) { toast.error('رقم الخزانة مطلوب — لكل سرير خزانة'); return; }
    try {
      await api.post(`/housings/${housing.id}/rooms/${roomId}/beds`, {
        bed_number: num,
        bed_type: newBed.bed_type,
        price: Number(newBed.price || 0),
        locker_number: locker,
      });
      setNewBed({ bed_number: '', bed_type: 'lower', price: '500', locker_number: '' });
      toast.success('تمت إضافة السرير');
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل إضافة السرير');
    }
  };

  const saveBedEdit = async () => {
    if (!editingBed) return;
    const locker = (editingBed.locker_number || '').trim();
    if (!locker) { toast.error('رقم الخزانة مطلوب — لكل سرير خزانة'); return; }
    try {
      await api.put(`/housings/${housing.id}/beds/${editingBed.id}`, {
        bed_number: editingBed.bed_number,
        bed_type: editingBed.bed_type,
        price: Number(editingBed.price || 0),
        locker_number: locker,
      });
      toast.success('تم حفظ السرير');
      setEditingBed(null);
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحفظ');
    }
  };

  const deleteBed = async (bed) => {
    if (bed.is_occupied) { toast.error('لا يمكن حذف سرير مشغول'); return; }
    if (!window.confirm(`حذف السرير "${bed.bed_number}"؟`)) return;
    try {
      await api.delete(`/housings/${housing.id}/beds/${bed.id}`);
      toast.success('تم حذف السرير');
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحذف');
    }
  };

  // Group beds by room
  const bedsByRoom = React.useMemo(() => {
    const g = {};
    beds.forEach((b) => {
      if (!g[b.room_id]) g[b.room_id] = [];
      g[b.room_id].push(b);
    });
    return g;
  }, [beds]);

  if (!housing) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DoorOpen className="w-5 h-5 text-amber-500" />
            إدارة الغرف والأسرّة — {housing.name}
          </DialogTitle>
          <DialogDescription>
            نظّم غرف السكن وأسرَّتها. كل سرير له نوع (علوي/سفلي) وسعر مستقل ورقم خزانة إلزامي (1 سرير ↔ 1 خزانة).
          </DialogDescription>
        </DialogHeader>

        {/* Add Room */}
        <div className="bg-secondary/30 border border-white/5 rounded-lg p-4 mb-4">
          <div className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Plus className="w-4 h-4 text-emerald-500" />
            إضافة غرفة جديدة
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <Input
              placeholder="اسم الغرفة"
              value={newRoom.name}
              onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
              className="bg-secondary/50 sm:col-span-2"
              data-testid="new-room-name"
            />
            <Input
              type="number"
              placeholder="السعة"
              value={newRoom.capacity}
              onChange={(e) => setNewRoom({ ...newRoom, capacity: e.target.value })}
              className="bg-secondary/50"
              data-testid="new-room-capacity"
            />
            <Button onClick={addRoom} className="gap-1" data-testid="add-room-btn">
              <Plus className="w-4 h-4" /> إضافة
            </Button>
          </div>
        </div>

        {/* Rooms list */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : rooms.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            لا توجد غرف بعد — أضف أول غرفة أعلاه
          </div>
        ) : (
          <div className="space-y-3">
            {rooms.map((room) => {
              const isExpanded = expandedRoom === room.id;
              const roomBeds = bedsByRoom[room.id] || [];
              return (
                <div key={room.id} className="border border-white/10 rounded-lg overflow-hidden" data-testid={`room-item-${room.id}`}>
                  {/* Room header */}
                  <div className="bg-secondary/40 p-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setExpandedRoom(isExpanded ? null : room.id)}
                      className="p-1 hover:bg-white/5 rounded"
                      data-testid={`expand-room-${room.id}`}
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                    {editingRoom?.id === room.id ? (
                      <>
                        <Input
                          value={editingRoom.name}
                          onChange={(e) => setEditingRoom({ ...editingRoom, name: e.target.value })}
                          className="bg-secondary/70 h-8 flex-1"
                          data-testid={`room-edit-name-${room.id}`}
                        />
                        <Input
                          type="number"
                          value={editingRoom.capacity}
                          onChange={(e) => setEditingRoom({ ...editingRoom, capacity: e.target.value })}
                          className="bg-secondary/70 h-8 w-20"
                          placeholder="السعة"
                        />
                        <Button size="sm" onClick={saveRoomEdit} data-testid={`save-room-${room.id}`}>حفظ</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingRoom(null)}>إلغاء</Button>
                      </>
                    ) : (
                      <>
                        <DoorOpen className="w-4 h-4 text-amber-500" />
                        <div className="flex-1">
                          <div className="font-semibold">{room.name}</div>
                          <div className="text-[10px] text-muted-foreground">السعة: {room.capacity || '—'}</div>
                        </div>
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/30">
                          <BedIcon className="w-3 h-3 me-1" />
                          {room.beds_occupied}/{room.beds_total}
                        </Badge>
                        <Button size="sm" variant="ghost" onClick={() => setEditingRoom({ ...room })} data-testid={`edit-room-${room.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteRoom(room)} className="text-red-500" data-testid={`delete-room-${room.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Beds list (expanded) */}
                  {isExpanded && (
                    <div className="bg-background p-3 space-y-2">
                      {roomBeds.length === 0 ? (
                        <div className="text-xs text-muted-foreground text-center py-2">
                          لا توجد أسرّة في هذه الغرفة
                        </div>
                      ) : (
                        roomBeds.map((bed) => (
                          <BedRow
                            key={bed.id}
                            bed={bed}
                            isEditing={editingBed?.id === bed.id}
                            editingBed={editingBed}
                            setEditingBed={setEditingBed}
                            onSave={saveBedEdit}
                            onDelete={() => deleteBed(bed)}
                          />
                        ))
                      )}

                      {/* Add bed form */}
                      <div className="pt-3 mt-2 border-t border-white/10 bg-secondary/20 rounded-md p-3">
                        <div className="text-xs text-amber-400 font-semibold mb-2">➕ إضافة سرير جديد لهذه الغرفة</div>
                        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                          <div>
                            <label className="block text-[10px] text-muted-foreground mb-1">رقم السرير *</label>
                            <Input
                              placeholder="مثال: A1"
                              value={newBed.bed_number}
                              onChange={(e) => setNewBed({ ...newBed, bed_number: e.target.value })}
                              className="bg-secondary/50 h-8 text-sm"
                              data-testid={`new-bed-number-${room.id}`}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-muted-foreground mb-1">النوع *</label>
                            <Select value={newBed.bed_type} onValueChange={(v) => setNewBed({ ...newBed, bed_type: v })}>
                              <SelectTrigger className="bg-secondary/50 h-8 text-sm" data-testid={`new-bed-type-${room.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="lower">سفلي</SelectItem>
                                <SelectItem value="upper">علوي</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-[10px] text-muted-foreground mb-1">السعر (د.إ) *</label>
                            <Input
                              type="number"
                              placeholder="500"
                              value={newBed.price}
                              onChange={(e) => setNewBed({ ...newBed, price: e.target.value })}
                              className="bg-secondary/50 h-8 text-sm"
                              data-testid={`new-bed-price-${room.id}`}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-muted-foreground mb-1">رقم الخزانة *</label>
                            <Input
                              placeholder="مثال: L1"
                              value={newBed.locker_number}
                              onChange={(e) => setNewBed({ ...newBed, locker_number: e.target.value })}
                              className="bg-secondary/50 h-8 text-sm"
                              data-testid={`new-bed-locker-${room.id}`}
                              required
                            />
                          </div>
                          <div className="flex items-end">
                            <Button size="sm" onClick={() => addBed(room.id)} className="w-full gap-1 h-8" data-testid={`add-bed-btn-${room.id}`}>
                              <Plus className="w-4 h-4" /> إضافة
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// One row = one bed, with inline edit toggle.
const BedRow = ({ bed, isEditing, editingBed, setEditingBed, onSave, onDelete }) => {
  if (isEditing) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-center bg-secondary/30 rounded-md p-2">
        <Input
          value={editingBed.bed_number}
          onChange={(e) => setEditingBed({ ...editingBed, bed_number: e.target.value })}
          className="bg-secondary/70 h-8 text-sm"
          data-testid={`edit-bed-number-${bed.id}`}
        />
        <Select value={editingBed.bed_type} onValueChange={(v) => setEditingBed({ ...editingBed, bed_type: v })}>
          <SelectTrigger className="bg-secondary/70 h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="lower">سفلي</SelectItem>
            <SelectItem value="upper">علوي</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="number"
          value={editingBed.price}
          onChange={(e) => setEditingBed({ ...editingBed, price: e.target.value })}
          className="bg-secondary/70 h-8 text-sm"
        />
        <Input
          value={editingBed.locker_number || ''}
          onChange={(e) => setEditingBed({ ...editingBed, locker_number: e.target.value })}
          className="bg-secondary/70 h-8 text-sm"
          placeholder="رقم الخزانة *"
          required
        />
        <Button size="sm" onClick={onSave} data-testid={`save-bed-${bed.id}`}>حفظ</Button>
        <Button size="sm" variant="outline" onClick={() => setEditingBed(null)}>إلغاء</Button>
      </div>
    );
  }
  const typeAr = bed.bed_type === 'upper' ? 'علوي' : 'سفلي';
  const typeColor = bed.bed_type === 'upper' ? 'text-purple-400 bg-purple-500/10 border-purple-500/30' : 'text-blue-400 bg-blue-500/10 border-blue-500/30';
  return (
    <div className="flex items-center gap-2 bg-secondary/20 rounded-md p-2" data-testid={`bed-row-${bed.id}`}>
      <BedIcon className="w-4 h-4 text-muted-foreground" />
      <div className="flex-1 flex items-center gap-3 flex-wrap text-sm">
        <span className="font-semibold">سرير #{bed.bed_number}</span>
        <Badge className={typeColor + ' border'}>{typeAr}</Badge>
        <span className="text-emerald-500 font-medium">
          <CurrencyLabel amount={bed.price} />
        </span>
        {bed.locker_number && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Package className="w-3 h-3" /> خزانة {bed.locker_number}
          </span>
        )}
        {bed.is_occupied ? (
          <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">
            مشغول {bed.current_resident_name ? `— ${bed.current_resident_name}` : ''}
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30">فارغ</Badge>
        )}
      </div>
      <Button size="sm" variant="ghost" onClick={() => setEditingBed({ ...bed })} data-testid={`edit-bed-${bed.id}`}>
        <Edit3 className="w-4 h-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onDelete}
        disabled={bed.is_occupied}
        className={`text-red-500 ${bed.is_occupied ? 'opacity-40 cursor-not-allowed' : ''}`}
        data-testid={`delete-bed-${bed.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default RoomsAndBedsManager;
